import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { native } from "../lib/native";
import { Trophy, X } from "lucide-react";

/**
 * Full-screen celebration overlay for publisher tier level-ups.
 * - Framer-motion driven confetti particles (no external dep needed)
 * - Native haptic burst on mount ("success" pattern)
 * - Auto-dismisses after 4.5s; click X or background to close early
 *
 * Props: { tier, botName, onClose }
 */
const TIER_META = {
  bronze:   { label: "BRONZE",   from: "#c86e3d", to: "#8a4923", accent: "#f4a97a" },
  silver:   { label: "SILVER",   from: "#c8d0da", to: "#8a95a6", accent: "#eef1f6" },
  gold:     { label: "GOLD",     from: "#f4c542", to: "#b3860f", accent: "#ffe084" },
  platinum: { label: "PLATINUM", from: "#e6eaf2", to: "#8c95ab", accent: "#ffffff" },
};

// Deterministic confetti positions (seeded per session, not per render)
function useParticles(n) {
  return useMemo(() => {
    return Array.from({ length: n }, (_, i) => ({
      id: i,
      x: (Math.random() - 0.5) * 100,
      rot: Math.random() * 720 - 360,
      delay: Math.random() * 0.5,
      dur: 2 + Math.random() * 2,
      size: 6 + Math.random() * 10,
      hue: Math.floor(Math.random() * 360),
      shape: Math.random() > 0.5 ? "square" : "circle",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
}

export default function TierCelebration({ tier, botName, onClose }) {
  const meta = TIER_META[tier] || TIER_META.bronze;
  const [dismissed, setDismissed] = useState(false);
  const particles = useParticles(60);

  const doClose = () => {
    setDismissed(true);
    onClose && onClose();
  };

  useEffect(() => {
    // Native OS-level celebration haptic. Falls back to navigator.vibrate.
    native.haptic("success");
    const t = setTimeout(() => native.haptic("light"), 220);
    const t2 = setTimeout(() => native.haptic("light"), 460);
    const dismiss = setTimeout(doClose, 4500);
    const onKey = (e) => { if (e.key === "Escape") doClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t); clearTimeout(t2); clearTimeout(dismiss);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  if (dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="tier-celebration"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={doClose}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-hidden"
        data-testid="tier-celebration"
      >
        {/* Confetti burst */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {particles.map((p) => (
            <motion.div
              key={p.id}
              initial={{ x: 0, y: -20, opacity: 0, rotate: 0 }}
              animate={{
                x: `${p.x}vw`,
                y: "110vh",
                opacity: [0, 1, 1, 0],
                rotate: p.rot,
              }}
              transition={{ duration: p.dur, delay: p.delay, ease: "easeOut" }}
              className="absolute left-1/2 top-0"
              style={{
                width: p.size, height: p.size,
                backgroundColor: `hsl(${p.hue} 80% 60%)`,
                borderRadius: p.shape === "circle" ? "50%" : 2,
                boxShadow: `0 0 12px hsl(${p.hue} 80% 60% / 0.6)`,
              }}
            />
          ))}
        </div>

        {/* Card */}
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.5, y: 40, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.7, opacity: 0 }}
          transition={{ type: "spring", damping: 14, stiffness: 240 }}
          className="relative max-w-md w-[90vw] mx-4 rounded-3xl p-8 text-center shadow-2xl"
          style={{
            background: `linear-gradient(135deg, ${meta.from} 0%, ${meta.to} 100%)`,
            color: "#0a0a0b",
            border: `2px solid ${meta.accent}`,
          }}
          data-testid={`tier-celebration-card-${tier}`}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full opacity-40 hover:opacity-100 transition-opacity"
            data-testid="tier-celebration-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <motion.div
            initial={{ scale: 0.4, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 8, stiffness: 200, delay: 0.15 }}
            className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: meta.accent, boxShadow: `0 0 32px ${meta.accent}` }}
          >
            <Trophy className="w-10 h-10" style={{ color: meta.to }} />
          </motion.div>

          <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-70 mb-1">Tier unlocked</div>
          <div className="font-display text-4xl font-black tracking-tight mb-2" data-testid="tier-celebration-tier">
            {meta.label}
          </div>
          {botName && (
            <div className="text-sm font-semibold opacity-80 mb-3" data-testid="tier-celebration-bot">
              {botName}
            </div>
          )}
          <div className="text-xs opacity-70 leading-relaxed max-w-xs mx-auto">
            Your verified track record just crossed the {meta.label.toLowerCase()} threshold.
            Followers can now filter your bot in the leaderboard.
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
